import { useEffect, useRef, useState } from 'react';
import { useStore } from '../store';
import { invalidateMembersCache } from '../utils/members-cache';
import { useT } from '../utils/i18n';
import styles from './UserProfileCard.module.css';

const PRESETS = Array.from({ length: 7 }, (_, index) => `/avatars/preset-${index + 1}.jpeg`);
const MAX_AVATAR_BYTES = 2 * 1024 * 1024;
const ALLOWED_AVATAR_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);

interface UserProfileCardProps {
  onClose: () => void;
}

export function UserProfileCard({ onClose }: UserProfileCardProps) {
  const t = useT();
  const token = useStore((state) => state.token);
  const userName = useStore((state) => state.userName);
  const userEmail = useStore((state) => state.userEmail);
  const userAvatar = useStore((state) => state.userAvatar);
  const fileRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [nameDraft, setNameDraft] = useState(userName || localStorage.getItem('novax:displayName') || 'User');
  const [avatarDraft, setAvatarDraft] = useState(userAvatar || localStorage.getItem('novax:userAvatar') || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);

  const initialName = userName || localStorage.getItem('novax:displayName') || 'User';
  const initialAvatar = userAvatar || localStorage.getItem('novax:userAvatar') || '';
  const trimmedName = nameDraft.trim();
  const hasChanges = trimmedName !== initialName || avatarDraft !== initialAvatar;
  const initial = trimmedName ? trimmedName.charAt(0).toUpperCase() : 'U';

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (showAvatarPicker) {
        setShowAvatarPicker(false);
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, showAvatarPicker]);

  const openAvatarPicker = () => {
    setError('');
    setShowAvatarPicker(true);
  };

  const handleAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setSaved(false);
    if (!ALLOWED_AVATAR_TYPES.has(file.type)) {
      setError(t('profile.imageTypeError'));
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setError(t('profile.imageSizeError'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setAvatarDraft(reader.result);
        setError('');
        setShowAvatarPicker(false);
      }
    };
    reader.onerror = () => setError(t('profile.imageReadError'));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!trimmedName || trimmedName.length > 80 || saving || !hasChanges) return;
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      if (token) {
        const response = await fetch('/auth/profile', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: trimmedName, avatarUrl: avatarDraft }),
        });
        if (!response.ok) throw new Error(`Profile update failed (${response.status})`);
      }

      localStorage.setItem('novax:displayName', trimmedName);
      localStorage.setItem('novax:userName', trimmedName);
      localStorage.setItem('novax:userAvatar', avatarDraft);
      useStore.setState({ userName: trimmedName, userAvatar: avatarDraft });
      invalidateMembersCache();
      setNameDraft(trimmedName);
      setSaved(true);
    } catch {
      setError(t('profile.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.dismissLayer} onMouseDown={onClose}>
      <section
        className={styles.card}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-card-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <div>
            <span className={styles.eyebrow}>PROFILE</span>
            <h2 id="profile-card-title">{t('profile.title')}</h2>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label={t('profile.close')}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={styles.identityRow}>
          <button className={styles.avatarEditor} onClick={openAvatarPicker} aria-label={t('detail.changeAvatar')}>
            {avatarDraft ? (
              <img src={avatarDraft} alt="" />
            ) : (
              <span>{initial}</span>
            )}
            <span className={styles.avatarEditIcon} aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                <path d="m15 5 4 4" />
              </svg>
            </span>
          </button>
          <div className={styles.identityText}>
            <strong>{trimmedName || t('general.displayNamePlaceholder')}</strong>
            <span>{userEmail}</span>
            <button className={styles.uploadLink} onClick={openAvatarPicker}>{t('detail.changeAvatar')}</button>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className={styles.hiddenInput}
            onChange={handleAvatarUpload}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="profile-display-name">{t('general.displayName')}</label>
          <input
            ref={nameInputRef}
            id="profile-display-name"
            value={nameDraft}
            maxLength={80}
            placeholder={t('general.displayNamePlaceholder')}
            onChange={(event) => {
              setNameDraft(event.target.value);
              setSaved(false);
              setError('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void handleSave();
            }}
          />
        </div>

        <div className={styles.footer}>
          <div className={styles.status} aria-live="polite">
            {error ? <span className={styles.error}>{error}</span> : saved ? <span className={styles.saved}>✓ {t('general.saved')}</span> : null}
          </div>
          <button className={styles.saveBtn} onClick={() => void handleSave()} disabled={!trimmedName || !hasChanges || saving}>
            {saving ? t('detail.saving') : t('detail.save')}
          </button>
        </div>

        {showAvatarPicker && (
          <div className={styles.pickerBackdrop} onMouseDown={() => setShowAvatarPicker(false)}>
            <div
              className={styles.avatarPickerCard}
              role="dialog"
              aria-modal="true"
              aria-labelledby="avatar-picker-title"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className={styles.pickerHeader}>
                <div>
                  <span className={styles.eyebrow}>AVATAR</span>
                  <h3 id="avatar-picker-title">{t('detail.changeAvatar')}</h3>
                </div>
                <button
                  className={styles.closeBtn}
                  onClick={() => setShowAvatarPicker(false)}
                  aria-label={t('detail.cancel')}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <button className={styles.uploadChoiceBtn} onClick={() => fileRef.current?.click()}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 16V4m0 0L7 9m5-5 5 5" />
                  <path d="M5 14v5h14v-5" />
                </svg>
                {t('detail.uploadImage')}
              </button>

              <div className={styles.pickerDivider}>
                <span>{t('detail.choosePreset')}</span>
              </div>

              <div className={styles.presetGrid}>
                {PRESETS.map((src, index) => (
                  <button
                    key={src}
                    className={`${styles.presetBtn} ${avatarDraft === src ? styles.presetBtnActive : ''}`}
                    onClick={() => {
                      setAvatarDraft(src);
                      setSaved(false);
                      setError('');
                      setShowAvatarPicker(false);
                    }}
                    aria-label={`${t('profile.presetAvatar')} ${index + 1}`}
                  >
                    <img src={src} alt="" />
                  </button>
                ))}
              </div>

              {error && <span className={styles.pickerError} role="alert">{error}</span>}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
